import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Outlets() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", gstin: "", phone: "", state_code: "27", fssai_no: "", upi_id: "", upi_payee_name: "" });

  const load = () => api.get("/outlets").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm({ name: "", address: "", gstin: "", phone: "", state_code: "27", fssai_no: "", upi_id: "", upi_payee_name: "" }); setOpen(true); };
  const startEdit = (o) => { setEditing(o); setForm({ name: o.name, address: o.address || "", gstin: o.gstin || "", phone: o.phone || "", state_code: o.state_code || "27", fssai_no: o.fssai_no || "", upi_id: o.upi_id || "", upi_payee_name: o.upi_payee_name || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/outlets/${editing.id}`, form);
      else await api.post("/outlets", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (o) => {
    if (!window.confirm(`Delete ${o.name}?`)) return;
    try { await api.delete(`/outlets/${o.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="outlets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Outlets</h1>
          <p className="text-sm text-[#7A7873] mt-1">Manage branches with their GSTIN & state codes.</p>
        </div>
        <Button onClick={startNew} className="bg-[#D95D39] hover:bg-[#C24F2C]" data-testid="add-outlet-btn">
          <Plus size={16} className="mr-1.5" /> Add Outlet
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((o) => (
          <div key={o.id} className="bg-white border border-[#EBE8E2] rounded-lg p-5" data-testid={`outlet-${o.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-md bg-[#FBE9E2] text-[#D95D39] flex items-center justify-center">
                <Building2 size={20} />
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(o)}><Pencil size={13} /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => del(o)}><Trash2 size={13} /></Button>
              </div>
            </div>
            <div className="font-heading font-bold text-lg text-[#2D2C2A]">{o.name}</div>
            <div className="text-sm text-[#7A7873] mt-1">{o.address || "—"}</div>
            <div className="text-xs text-[#7A7873] mt-2 font-mono-num">GSTIN: {o.gstin || "—"}</div>
            <div className="text-xs text-[#7A7873] font-mono-num">Phone: {o.phone || "—"}</div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{editing ? "Edit Outlet" : "Add Outlet"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="outlet-name" /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
              <div><Label>State Code</Label><Input value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} /></div>
            </div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>FSSAI License No.</Label><Input value={form.fssai_no} onChange={(e) => setForm({ ...form, fssai_no: e.target.value })} placeholder="14-digit FSSAI number" data-testid="outlet-fssai" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>UPI ID</Label><Input value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="restaurant@upi" data-testid="outlet-upi-id" /></div>
              <div><Label>UPI Payee Name</Label><Input value={form.upi_payee_name} onChange={(e) => setForm({ ...form, upi_payee_name: e.target.value })} placeholder="Restaurant Name" /></div>
            </div>
            <p className="text-[11px] text-[#7A7873]">UPI ID + Payee Name appear as a scan-to-pay QR on the printed bill (amount auto-filled).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={save} data-testid="save-outlet">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
